import React from 'react'
import Modal from 'components/Modal'
import i18n from 'components/i18n'
import { BulkUserJob, BulkUserOutcomeRow } from 'mutations/bulk_user_job'

type BulkDisableResultModalProps = {
  open: boolean,
  job: BulkUserJob|null,
  onClose: () => void,
}

const Stat = (props: {value: number, label: string, tone?: 'neutral'|'good'|'bad'}) => {
  const tone = props.tone || 'neutral'
  const valueColor = props.value === 0 && tone !== 'good'
    ? 'text-lightgray'
    : tone === 'bad' ? 'text-coral' : tone === 'good' ? 'text-listenblue' : 'text-deepgray'

  return <div className="flex-1 rounded-lg bg-alabastergray px-4 py-3 text-center">
    <div className={`text-3xl font-bold leading-none ${valueColor}`}>{props.value}</div>
    <div className="mt-1 text-xs uppercase tracking-wide text-medgray">{props.label}</div>
  </div>
}

/**
 * Rows arrive one per user, but the reason text repeats heavily — a hundred people
 * skipped for the same cause should read as one line with a count, not a hundred
 * identical lines in a scroll box.
 */
const fallbackLabel = (row: BulkUserOutcomeRow) => {
  // The server writes `message` to be shown as-is; these only cover it being absent.
  switch (row.reason) {
    case 'self': return i18n.t('bulk_result_reason_self')
    case 'notInScope': return i18n.t('bulk_result_reason_not_in_scope')
    case 'notFound': return i18n.t('bulk_result_reason_not_found')
    case 'transientError': return i18n.t('bulk_result_reason_transient')
    default: return i18n.t('bulk_result_reason_unknown')
  }
}

type OutcomeGroup = {label: string, users: string[]}

const groupByMessage = (rows: BulkUserOutcomeRow[]): OutcomeGroup[] => {
  // Plain object rather than a Map: the build targets es6 without downlevelIteration,
  // and spreading a Map iterator transpiles to an array-like helper that yields
  // undefined entries.
  const order: string[] = []
  const byLabel: {[label: string]: OutcomeGroup} = {}

  rows.forEach((row) => {
    const label = row.message || fallbackLabel(row)
    const who = row.userEmail || row.userId
    if (!byLabel[label]) {
      byLabel[label] = {label, users: []}
      order.push(label)
    }
    byLabel[label].users.push(who)
  })

  return order.map(l => byLabel[l]).sort((a, b) => b.users.length - a.users.length)
}

const OutcomeGroups = (props: {
  rows: BulkUserOutcomeRow[], heading: string, tone: 'bad'|'neutral',
}) => {
  if (!props.rows.length) return null
  const groups = groupByMessage(props.rows)

  const total = props.rows.length

  return <div>
    <div className="flex flex-row items-baseline gap-2">
      <span className={`text-sm font-bold ${props.tone === 'bad' ? 'text-coral' : 'text-deepgray'}`}>
        {props.heading}
      </span>
      <span className="text-xs text-medgray">
        {i18n.t('bulk_result_user_count', {count: total})}
      </span>
    </div>
    <ul className="mt-2 flex flex-col divide-y divide-lightwarmgray max-h-64 overflow-y-auto
      rounded-lg border border-lightwarmgray">
      {groups.map((g) => {
        const users = g.users || []
        return <li key={g.label} className="text-sm px-3 py-2">
          <div className="flex flex-row items-baseline gap-2">
            <span className={`flex-1 ${props.tone === 'bad' ? 'text-deepgray' : 'text-deepgray'}`}>
              {g.label}
            </span>
            {users.length > 1 && <span
              className="shrink-0 rounded-full bg-lightwarmgray px-2 text-xs text-medgray"
            >
              {users.length}
            </span>}
          </div>
          {users.length > 0 && <div
            className="mt-0.5 text-xs text-medgray truncate"
            title={users.join(', ')}
          >
            {users.slice(0, 3).join(', ')}
            {users.length > 3 && i18n.t('bulk_result_and_more', {count: users.length - 3})}
          </div>}
        </li>
      })}
    </ul>
  </div>
}

const BulkDisableResultModal = (props: BulkDisableResultModalProps) => {
  const job = props.job
  if (!job) return null

  const isDisable = job.action === 'disable'
  const succeeded = isDisable ? job.counts.disabled : job.counts.enabled
  // Successes are never in `outcomes` — only skips and failures are.
  const rows = job.outcomes || []
  const affected = job.affectedUsers || []
  const failures = rows.filter(r => r.outcome === 'failed')
  const skips = rows.filter(r => r.outcome === 'skipped')
  const inProgress = job.status !== 'done'
  const nothingHappened = !inProgress && succeeded === 0

  return <Modal
    open={props.open}
    onClose={props.onClose}
    title={inProgress
      ? i18n.t('bulk_result_title_running')
      : i18n.t('bulk_result_title_done')}
    content={[{
      element: <div className="w-[36rem] max-w-full text-left flex flex-col gap-5">
        {inProgress && <div className="rounded-lg bg-alabastergray px-4 py-3">
          <div className="flex flex-row items-baseline justify-between mb-2">
            <span className="text-sm font-bold text-deepgray">
              {isDisable
                ? i18n.t('bulk_progress_suspending', {count: job.total})
                : i18n.t('bulk_progress_enabling', {count: job.total})}
            </span>
            <span className="text-2xl font-bold text-listenblue leading-none">
              {job.progress}%
            </span>
          </div>
          <div className="h-2 w-full rounded-full bg-white overflow-hidden">
            <div
              className="h-full bg-listenblue transition-all duration-500"
              style={{width: `${job.progress}%`}}
            />
          </div>
          <div className="mt-2 text-sm text-medgray">
            {i18n.t('bulk_result_progress', {
              processed: job.processed,
              total: job.total,
              progress: job.progress,
              remaining: Math.max(0, job.total - job.processed),
            })}
          </div>
        </div>}

        <div className="flex flex-row gap-3">
          <Stat
            value={succeeded}
            tone={succeeded > 0 ? 'good' : 'neutral'}
            label={isDisable ? i18n.t('bulk_result_disabled') : i18n.t('bulk_result_enabled')}
          />
          <Stat value={job.counts.skipped} label={i18n.t('bulk_result_skipped')} />
          <Stat
            value={job.counts.failed}
            tone={job.counts.failed > 0 ? 'bad' : 'neutral'}
            label={i18n.t('bulk_result_failed')}
          />
        </div>

        {/* Every user was skipped, so say so plainly instead of leaving a row of zeroes. */}
        {nothingHappened && <div className="rounded-lg bg-deadyellow px-3 py-2 text-sm text-deepgray">
          {isDisable
            ? i18n.t('bulk_result_none_disabled')
            : i18n.t('bulk_result_none_enabled')}
        </div>}

        <OutcomeGroups
          rows={failures}
          heading={i18n.t('bulk_result_failures_heading')}
          tone="bad"
        />
        <OutcomeGroups
          rows={skips}
          heading={i18n.t('bulk_result_skips_heading')}
          tone="neutral"
        />

        {job.outcomesTruncated && <div className="text-xs text-medgray italic">
          {i18n.t('bulk_result_truncated')}
        </div>}

        {/*
          Everyone the job acted on, successes included — the durable record behind the
          audit criterion. Only getBulkUserJob returns it, so it is absent while a job
          is still running and on rows that have not been opened.
        */}
        {!!affected.length && <details className="text-sm">
          <summary className="cursor-pointer font-bold text-deepgray">
            {i18n.t('bulk_result_affected_heading')} ({affected.length})
          </summary>
          <ul className="mt-2 max-h-40 overflow-y-auto text-xs text-medgray">
            {affected.map(r => (
              <li key={`a-${r.userId}`}>{r.userEmail || r.userId}</li>
            ))}
          </ul>
          {job.affectedUsersTruncated && <div className="mt-1 text-xs text-medgray italic">
            {i18n.t('bulk_result_affected_truncated')}
          </div>}
        </details>}

        <div className="text-xs text-medgray border-t border-lightwarmgray pt-3">
          {inProgress ? i18n.t('bulk_result_email_note') : i18n.t('bulk_result_email_note_done')}
        </div>
      </div>,
    }]}
    actions={[{text: i18n.t('close'), color: 'primary', getActionEvent: props.onClose}]}
  />
}

export default BulkDisableResultModal
